import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Icon, type IconName } from './Icon'
import { ToastContext, type ShowToast } from './toastContext'
import styles from './Toast.module.css'

const TOAST_DURATION_MS = 2600

interface ToastState {
  id: number
  text: string
  icon: IconName
}

/** Single slot: a new toast replaces the current one and restarts the 2600ms timer. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ToastState | null>(null)
  const timer = useRef<number | undefined>(undefined)
  const seq = useRef(0)

  const show = useCallback<ShowToast>((text, options) => {
    window.clearTimeout(timer.current)
    seq.current += 1
    setToast({ id: seq.current, text, icon: options?.icon ?? 'check' })
    timer.current = window.setTimeout(() => setToast(null), TOAST_DURATION_MS)
  }, [])

  useEffect(() => () => window.clearTimeout(timer.current), [])

  const value = useMemo(() => show, [show])

  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <div role="status" aria-live="polite">
          {toast && (
            <div key={toast.id} className={styles.toast}>
              <Icon name={toast.icon} size={18} color="var(--color-primary-light)" />
              {toast.text}
            </div>
          )}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  )
}
